import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LoadErrorScreen from './LoadErrorScreen';

const API = process.env.REACT_APP_SERVER_URL;

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('ru-RU');
  } catch {
    return '—';
  }
};

function getPassportSeries(pd) {
  return String(pd?.passportSeries ?? pd?.series ?? '').trim();
}

function getPassportNumber(pd) {
  return String(pd?.passportNumber ?? pd?.number ?? '').trim();
}

function profileGapsFromUser(user) {
  if (!user) return ['fullName', 'passportSeries', 'passportNumber', 'phoneNumber'];
  const pd = user.passportData || {};
  const gaps = [];
  if (!String(pd.fullName || '').trim()) gaps.push('fullName');
  if (!getPassportSeries(pd)) gaps.push('passportSeries');
  if (!getPassportNumber(pd)) gaps.push('passportNumber');
  if (!String(user.phoneNumber || '').trim()) gaps.push('phoneNumber');
  return gaps;
}

const TourBookingPage = () => {
  const { tourNumber } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const periodParam = searchParams.get('period');
  const periodIndex = periodParam != null ? parseInt(periodParam, 10) : NaN;

  const [tour, setTour] = useState(null);
  const [loadingTour, setLoadingTour] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [tourError, setTourError] = useState('');

  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  const [fullName, setFullName] = useState('');
  const [passportSeries, setPassportSeries] = useState('');
  const [passportNumber, setPassportNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileFormError, setProfileFormError] = useState('');

  const [seats, setSeats] = useState(1);
  const [acceptedOffer, setAcceptedOffer] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [booking, setBooking] = useState(null);
  const [paymentComment, setPaymentComment] = useState('');
  const [markingPaid, setMarkingPaid] = useState(false);
  const [markPaidError, setMarkPaidError] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const loadTour = useCallback(() => {
    if (!tourNumber) return;
    setLoadingTour(true);
    setLoadError(false);
    setTourError('');
    fetch(`${API}/api/tours/public/${encodeURIComponent(tourNumber)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (res.status === 404) {
          setTourError('Тур не найден');
          return null;
        }
        if (!res.ok) throw new Error(data.message || 'Не удалось загрузить тур');
        return data;
      })
      .then((data) => {
        if (data) setTour(data);
        else setTour(null);
      })
      .catch((e) => {
        setLoadError(true);
        setTour(null);
      })
      .finally(() => setLoadingTour(false));
  }, [tourNumber]);

  const loadProfile = useCallback(() => {
    const t = localStorage.getItem('token');
    if (!t) {
      setProfile(null);
      setLoadingProfile(false);
      return;
    }
    setLoadingProfile(true);
    fetch(`${API}/api/profile`, {
      headers: { Authorization: `Bearer ${t}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Профиль');
        return data;
      })
      .then((u) => {
        setProfile(u);
        const pd = u.passportData || {};
        setFullName(String(pd.fullName || '').trim());
        setPassportSeries(getPassportSeries(pd));
        setPassportNumber(getPassportNumber(pd));
        setPhoneNumber(String(u.phoneNumber || '').trim());
      })
      .catch(() => {
        setProfile(null);
      })
      .finally(() => setLoadingProfile(false));
  }, []);

  useEffect(() => {
    loadTour();
  }, [loadTour]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!token) setAuthDialogOpen(true);
    else setAuthDialogOpen(false);
  }, [token]);

  const period = useMemo(() => {
    if (!tour?.periods || !Number.isInteger(periodIndex) || periodIndex < 0) return null;
    return tour.periods[periodIndex] ?? null;
  }, [tour, periodIndex]);

  const maxSeats = period?.seatsAvailable ?? 0;

  useEffect(() => {
    if (maxSeats > 0) {
      setSeats((s) => Math.min(Math.max(1, s), maxSeats));
    }
  }, [maxSeats]);

  const gaps = useMemo(() => profileGapsFromUser(profile), [profile]);

  const prepaymentAmount = useMemo(() => {
    if (!tour || tour.price == null || tour.prepaymentPercent == null) return 0;
    const price = Number(tour.price);
    const pct = Number(tour.prepaymentPercent);
    const n = Number(seats);
    if (!Number.isFinite(price) || !Number.isFinite(pct) || !Number.isFinite(n)) return 0;
    return Math.round((price * n * pct) / 100);
  }, [tour, seats]);

  const totalPrice = useMemo(() => {
    if (!tour || tour.price == null) return 0;
    const price = Number(tour.price);
    const n = Number(seats);
    if (!Number.isFinite(price) || !Number.isFinite(n)) return 0;
    return Math.round(price * n);
  }, [tour, seats]);

  const handleSaveProfile = async () => {
    setProfileFormError('');
    const t = localStorage.getItem('token');
    if (!t) return;
    const phoneNorm = phoneNumber.replace(/\s/g, '');
    const mergedPassport = {
      ...(profile?.passportData || {}),
      fullName: fullName.trim(),
      passportSeries: passportSeries.trim(),
      passportNumber: passportNumber.trim(),
    };
    setProfileSaving(true);
    try {
      const res = await fetch(`${API}/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${t}`,
        },
        body: JSON.stringify({
          phoneNumber: phoneNorm,
          passportData: mergedPassport,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Не удалось сохранить');
      const nextUser = data.user || { ...profile, phoneNumber: phoneNorm, passportData: mergedPassport };
      setProfile(nextUser);
      if (profileGapsFromUser(nextUser).length === 0) {
        setProfileFormError('');
      }
    } catch (e) {
      setProfileFormError(e.message || 'Ошибка сохранения');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleCreateBooking = async () => {
    setSubmitError('');
    const t = localStorage.getItem('token');
    if (!t) return;
    if (!acceptedOffer) {
      setSubmitError('Примите условия договора оферты.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/tour-bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${t}`,
        },
        body: JSON.stringify({
          tourNumber,
          periodIndex,
          seats: Number(seats),
          acceptedOffer: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Не удалось создать бронь');
      setBooking(data);
    } catch (e) {
      setSubmitError(e.message || 'Ошибка');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkPrepaid = async () => {
    if (!booking?._id) return;
    setMarkPaidError('');
    const t = localStorage.getItem('token');
    if (!t) return;
    setMarkingPaid(true);
    try {
      const res = await fetch(`${API}/api/tour-bookings/${booking._id}/mark-prepaid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${t}`,
        },
        body: JSON.stringify({ comment: paymentComment }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Ошибка');
      setBooking(data.booking || data);
      setPaymentComment('');
    } catch (e) {
      setMarkPaidError(e.message || 'Ошибка');
    } finally {
      setMarkingPaid(false);
    }
  };

  if (loadingTour) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress sx={{ color: '#8B7D6B' }} />
      </Box>
    );
  }

  if (loadError) {
    return <LoadErrorScreen />;
  }

  if (tourError || !tour) {
    return (
      <Container maxWidth="sm" sx={{ mt: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {tourError || 'Тур не найден.'}
        </Alert>
        <Button component={RouterLink} to="/tours" startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none' }}>
          К списку туров
        </Button>
      </Container>
    );
  }

  if (!Number.isInteger(periodIndex) || periodIndex < 0 || !period) {
    return (
      <Container maxWidth="sm" sx={{ mt: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Укажите корректный заезд (параметр period в адресе).
        </Alert>
        <Button
          component={RouterLink}
          to={`/tours/${encodeURIComponent(tourNumber)}`}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none' }}
        >
          К туру
        </Button>
      </Container>
    );
  }

  const pay = booking?.operatorPaymentSnapshot || {};
  const operatorPhoneDisplay = String(
    pay.operatorContactPhone || pay.phoneForTransfer || ''
  ).trim();
  const showOperatorPhoneBlock =
    booking &&
    ['awaiting_prepayment_confirmation', 'completed'].includes(booking.status) &&
    operatorPhoneDisplay;

  return (
    <Container maxWidth="sm" sx={{ mt: 2, mb: 4 }}>
      <Button
        component={RouterLink}
        to={`/tours/${encodeURIComponent(tourNumber)}`}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        К туру
      </Button>

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, fontFamily: 'serif' }}>
        Бронирование
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {tour.title} • {formatDate(period.dateFrom)} — {formatDate(period.dateTo)}
      </Typography>

      <Dialog
        open={authDialogOpen}
        onClose={() => {
          setAuthDialogOpen(false);
          navigate(`/tours/${encodeURIComponent(tourNumber)}`);
        }}
      >
        <DialogTitle sx={{ fontFamily: 'serif' }}>Вход в аккаунт</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Чтобы забронировать тур, войдите или зарегистрируйтесь.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            component={RouterLink}
            to="/register"
            state={{
              redirectTo: `/tours/${encodeURIComponent(tourNumber)}/book?period=${periodIndex}`,
            }}
            sx={{ textTransform: 'none', color: '#8B7D6B' }}
          >
            Регистрация
          </Button>
          <Button
            component={RouterLink}
            to="/login"
            state={{
              redirectTo: `/tours/${encodeURIComponent(tourNumber)}/book?period=${periodIndex}`,
            }}
            variant="contained"
            sx={{ textTransform: 'none', backgroundColor: '#8B7D6B', '&:hover': { backgroundColor: '#6B5D4B' } }}
          >
            Войти
          </Button>
        </DialogActions>
      </Dialog>

      {!token ? null : loadingProfile ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} sx={{ color: '#8B7D6B' }} />
        </Box>
      ) : gaps.length > 0 ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: '#F5F3F0',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Заполните данные для бронирования
          </Typography>
          {profileFormError && (
            <Alert severity="error" onClose={() => setProfileFormError('')}>
              {profileFormError}
            </Alert>
          )}
          {gaps.includes('fullName') && (
            <TextField
              label="ФИО"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
            />
          )}
          {gaps.includes('passportSeries') && (
            <TextField
              label="Серия паспорта"
              value={passportSeries}
              onChange={(e) => setPassportSeries(e.target.value)}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
            />
          )}
          {gaps.includes('passportNumber') && (
            <TextField
              label="Номер паспорта"
              value={passportNumber}
              onChange={(e) => setPassportNumber(e.target.value)}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
            />
          )}
          {gaps.includes('phoneNumber') && (
            <TextField
              label="Телефон"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              fullWidth
              required
              placeholder="+79991234567"
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
            />
          )}
          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={profileSaving}
            sx={{
              textTransform: 'none',
              backgroundColor: '#8B7D6B',
              '&:hover': { backgroundColor: '#6B5D4B' },
            }}
          >
            {profileSaving ? 'Сохранение…' : 'Сохранить и продолжить'}
          </Button>
        </Box>
      ) : booking ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {booking.status === 'awaiting_prepayment_confirmation' && (
            <Alert severity="info">
              Статус: ожидает подтверждения предоплаты туроператором.
            </Alert>
          )}
          {booking.status === 'awaiting_prepayment' && (
            <Alert severity="warning">
              Вам необходимо внести предоплату в течении двух часов, в противном случае бронь будет отменена.
            </Alert>
          )}

          <Box sx={{ p: 2, backgroundColor: '#F5F3F0', borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Сумма предоплаты
            </Typography>
            <Typography variant="h5" sx={{ color: '#8B7D6B', fontWeight: 700 }}>
              {Number(booking.prepaymentAmount || 0).toLocaleString('ru-RU')} ₽
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Мест: {booking.seats} • Всего по туру: {Number(booking.totalPrice || 0).toLocaleString('ru-RU')} ₽
            </Typography>
          </Box>

          {showOperatorPhoneBlock && (
            <Box
              sx={{
                p: 2,
                backgroundColor: '#e8f5e9',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'rgba(46, 125, 50, 0.35)',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Телефон туроператора
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Для вопросов по бронированию после внесения предоплаты:
              </Typography>
              <Link
                href={`tel:${operatorPhoneDisplay.replace(/\s/g, '')}`}
                underline="hover"
                sx={{ fontWeight: 600, color: '#2e7d32', fontSize: '1.05rem' }}
              >
                {operatorPhoneDisplay}
              </Link>
            </Box>
          )}

          {booking.status === 'awaiting_prepayment' && (
            <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Реквизиты туроператора
              </Typography>
              {pay.recipientFullName && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Получатель:</strong> {pay.recipientFullName}
                </Typography>
              )}
              {pay.banks && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Банк:</strong> {pay.banks}
                </Typography>
              )}
              {pay.cardNumber && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Карта:</strong> {pay.cardNumber}
                </Typography>
              )}
              {pay.phoneForTransfer && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Телефон для перевода:</strong> {pay.phoneForTransfer}
                </Typography>
              )}
            </Box>
          )}

          {booking.status === 'awaiting_prepayment' && (
            <>
              {markPaidError && (
                <Alert severity="error" onClose={() => setMarkPaidError('')}>
                  {markPaidError}
                </Alert>
              )}
              <Typography variant="body2">
                Если платите с чужой карты, укажите в комментарии, от кого перевод.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Комментарий к платежу (необязательно)"
                value={paymentComment}
                onChange={(e) => setPaymentComment(e.target.value)}
                disabled={markingPaid}
              />
              <Button
                variant="contained"
                onClick={handleMarkPrepaid}
                disabled={markingPaid}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#8B7D6B',
                  '&:hover': { backgroundColor: '#6B5D4B' },
                }}
              >
                {markingPaid ? 'Отправка…' : 'Я внес предоплату'}
              </Button>
            </>
          )}

          <Button component={RouterLink} to="/tours" sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
            К каталогу туров
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: '#F5F3F0',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {maxSeats < 1 && (
            <Alert severity="warning">На выбранный заезд нет свободных мест.</Alert>
          )}
          {submitError && (
            <Alert severity="error" onClose={() => setSubmitError('')}>
              {submitError}
            </Alert>
          )}

          <TextField
            label="Количество мест"
            type="number"
            inputProps={{ min: 1, max: Math.max(1, maxSeats) }}
            value={seats}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!Number.isFinite(v)) return;
              setSeats(Math.min(Math.max(1, v), Math.max(1, maxSeats)));
            }}
            disabled={maxSeats < 1}
            fullWidth
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
          />

          <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Сумма тура ({seats} мест)
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {totalPrice.toLocaleString('ru-RU')} ₽
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Предоплата ({tour.prepaymentPercent ?? '—'}%)
            </Typography>
            <Typography variant="h6" sx={{ color: '#8B7D6B', fontWeight: 700 }}>
              {prepaymentAmount.toLocaleString('ru-RU')} ₽
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedOffer}
                onChange={(e) => setAcceptedOffer(e.target.checked)}
                sx={{ color: '#8B7D6B', '&.Mui-checked': { color: '#8B7D6B' } }}
              />
            }
            label={
              <Typography variant="body2">
                Я принимаю условия{' '}
                <Link component={RouterLink} to="/terms" target="_blank" rel="noopener noreferrer">
                  договора оферты
                </Link>
              </Typography>
            }
          />

          <Button
            variant="contained"
            disabled={!acceptedOffer || maxSeats < 1 || submitting}
            onClick={handleCreateBooking}
            sx={{
              textTransform: 'none',
              backgroundColor: '#8B7D6B',
              '&:hover': { backgroundColor: '#6B5D4B' },
            }}
          >
            {submitting ? 'Создание…' : 'Далее'}
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default TourBookingPage;
